import { Formik, useField } from 'formik';
import {
  CompanyPageCompanyFragment,
  useCompanyPageUpdateCompanyMutation,
} from 'generated/graphql';
import { useState } from 'react';
import {
  enableDisableRadioButtonOptions,
  EnableDisableValue,
} from 'views/Company/CompanyView/CompanyView.helpers';
import {
  EditFormWrapper,
  FormikRadioButtonGroup,
  InlineNotification,
  NotificationType,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import {
  CANDIDATE_PORTAL_DATA_TEST_ID,
  CANDIDATE_PORTAL_DISABLING_DISCLAIMER,
  CANDIDATE_PORTAL_ENABLED_LABEL,
  CANDIDATE_PORTAL_ENABLING_DISCLAIMER,
} from './CandidatePortalSection.constants';
import styles from './CandidatePortalSection.module.scss';

type CandidatePortalSectionFormValues = {
  isWhitelabelingEnabled: string;
};

type CandidatePortalSectionFormProps = {
  company: CompanyPageCompanyFragment;
  onCancelClick: () => void;
  onSave: () => void;
};

const CandidatePortalSectionForm = ({
  company,
  onCancelClick,
  onSave,
}: CandidatePortalSectionFormProps) => {
  const { handleMsgType } = useNotification();
  const [isDisclaimerVisible, setIsDisclaimerVisible] = useState(false);

  const [editCompanyMutation] = useCompanyPageUpdateCompanyMutation();

  const handleSave = (values: CandidatePortalSectionFormValues) => {
    editCompanyMutation({
      variables: {
        id: company.id,
        name: company.name,
        isWhitelabelingEnabled:
          values.isWhitelabelingEnabled === EnableDisableValue.Enabled,
      },
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          title: 'Candidate Portal settings updated.',
          dismissable: true,
        });
        onSave();
      },
      onError: () => {
        handleMsgType({
          type: TNotification.error,
          title: 'Candidate Portal settings could not be updated',
          message: 'Something went wrong. Please try again.',
          dismissable: true,
        });
      },
    });
  };

  const initialValues: CandidatePortalSectionFormValues = {
    isWhitelabelingEnabled: company.isWhitelabelingEnabled
      ? EnableDisableValue.Enabled
      : EnableDisableValue.Disabled,
  };

  return (
    <Formik<CandidatePortalSectionFormValues>
      initialValues={initialValues}
      onSubmit={handleSave}
    >
      {({ submitForm, values }) => (
        <EditFormWrapper
          onCancelClick={onCancelClick}
          onSaveClick={submitForm}
          title="Edit Candidate Portal"
          formId="candidate-portal-form"
          hasResetAllButton={false}
          data-testid={CANDIDATE_PORTAL_DATA_TEST_ID}
        >
          <form
            onSubmit={event => event.preventDefault()}
            id="candidate-portal-form-element"
            className={styles.form}
          >
            <div>
              <FormikRadioButtonGroup
                id="isWhitelabelingEnabled"
                name="isWhitelabelingEnabled"
                options={enableDisableRadioButtonOptions}
                layout="horizontal"
                label={CANDIDATE_PORTAL_ENABLED_LABEL}
                data-testid={`${CANDIDATE_PORTAL_DATA_TEST_ID}__enabled-radio-group`}
                useFormikField={useField}
                className={styles.radioGroup}
                onChange={() => setIsDisclaimerVisible(true)}
              />
              {isDisclaimerVisible && (
                <InlineNotification
                  notificationType={NotificationType.NEUTRAL}
                  className={styles.inlineNotification}
                >
                  {values.isWhitelabelingEnabled === EnableDisableValue.Enabled
                    ? CANDIDATE_PORTAL_ENABLING_DISCLAIMER
                    : CANDIDATE_PORTAL_DISABLING_DISCLAIMER}
                </InlineNotification>
              )}
            </div>
          </form>
        </EditFormWrapper>
      )}
    </Formik>
  );
};

export default CandidatePortalSectionForm;
